import React, { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { DndContext, DragEndEvent, PointerSensor, useDroppable, useSensor, useSensors } from '@dnd-kit/core';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { MoreVertical, Plus } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { CrmShell, PageHeader, Button, Modal } from '../../components/CrmShell';
import { EmptyState, Field, formatDate, formatMoney, inputClass, parseDateInput, dateInputValue } from '../../components/CrmForms';
import { Activity, AppUser, Company, Contact, Deal } from '../../types';
import {
  createActivity,
  createDeal,
  createReminder,
  deleteDeal,
  moveDealToStage,
  subscribeToActivitiesForDeal,
  subscribeToCompanies,
  subscribeToContacts,
  subscribeToDeals,
  subscribeToPipelineSettings,
  updateDeal,
} from '../../services/crm';
import { fetchUsers } from '../../services/users';
import { ActivityForm, ReminderForm } from '../ContactsPage';
import { logGlobalError } from '../../utils/globalErrorLogger';

export function DealsPage() {
  const { dealId } = useParams();
  return dealId ? <DealDetailPage dealId={dealId} /> : <DealsBoardPage />;
}

function DealsBoardPage() {
  const { currentUser } = useAuth();
  const [deals, setDeals] = useState<Deal[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [users, setUsers] = useState<AppUser[]>([]);
  const [stages, setStages] = useState<string[]>([]);
  const [editing, setEditing] = useState<Deal | 'new' | null>(null);
  const [reminderDeal, setReminderDeal] = useState<Deal | null>(null);
  const [error, setError] = useState<string | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  useEffect(() => {
    const unsubs = [
      subscribeToDeals(setDeals, (err) => setError(err.message)),
      subscribeToContacts(setContacts, (err) => setError(err.message)),
      subscribeToCompanies(setCompanies, (err) => setError(err.message)),
      subscribeToPipelineSettings((settings) => setStages(settings.stages), (err) => setError(err.message)),
    ];
    fetchUsers().then(setUsers).catch((err) => {
      setError(err.message);
      logGlobalError(err, { context: 'DealsPage.fetchUsers' });
    });
    return () => unsubs.forEach((unsub) => unsub());
  }, []);

  const contactsById = useMemo(() => new Map(contacts.map((item) => [item.id, item])), [contacts]);
  const companiesById = useMemo(() => new Map(companies.map((item) => [item.id, item])), [companies]);
  const usersById = useMemo(() => new Map(users.map((item) => [item.uid, item])), [users]);

  async function handleDragEnd(event: DragEndEvent) {
    const dealId = String(event.active.id);
    const stage = event.over?.id ? String(event.over.id) : null;
    const deal = deals.find((item) => item.id === dealId);
    if (!deal || !stage || deal.stage === stage) return;
    try {
      await moveDealToStage(dealId, stage);
    } catch (err: any) {
      setError(err?.message || 'Failed to move deal');
      logGlobalError(err, { context: 'DealsPage.moveDeal' });
    }
  }

  async function saveDeal(values: DealFormValues) {
    const target = editing;
    const payload = {
      ...values,
      contactId: values.contactId || null,
      companyId: values.companyId || null,
      ownerId: values.ownerId || null,
      value: Number(values.value) || 0,
    };
    setEditing(null);
    try {
      if (target && target !== 'new') await updateDeal(target.id, payload);
      else await createDeal(payload);
    } catch (err: any) {
      setError(err?.message || 'Failed to save deal');
      logGlobalError(err, { context: 'DealsPage.saveDeal' });
    }
  }

  async function saveReminder(values: { assignedTo: string; dueDate: Date; note: string }) {
    if (!reminderDeal) return;
    const entityId = reminderDeal.id;
    setReminderDeal(null);
    try {
      await createReminder({ ...values, entityType: 'deal', entityId }, currentUser?.uid ?? '');
    } catch (err: any) {
      setError(err?.message || 'Failed to save reminder');
      logGlobalError(err, { context: 'DealsPage.saveReminder' });
    }
  }

  return (
    <CrmShell wide>
      <PageHeader
        title="Pipeline"
        description="Track active deals and move them through the sales process."
        action={<Button onClick={() => setEditing('new')}><Plus className="mr-1 inline h-4 w-4" />New deal</Button>}
      />
      {error && <div className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {stages.length ? (
        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <div className="flex gap-4 overflow-x-auto pb-2">
            {stages.map((stage) => {
              const stageDeals = deals.filter((deal) => deal.stage === stage);
              return (
                <PipelineColumn key={stage} stage={stage} deals={stageDeals}>
                  {stageDeals.map((deal) => (
                    <DealCard
                      key={deal.id}
                      deal={deal}
                      contact={deal.contactId ? contactsById.get(deal.contactId) : undefined}
                      company={deal.companyId ? companiesById.get(deal.companyId) : undefined}
                      owner={deal.ownerId ? usersById.get(deal.ownerId) : undefined}
                      detailUrl={`/deals/${deal.id}`}
                      onEdit={() => setEditing(deal)}
                      onReminder={() => setReminderDeal(deal)}
                      onDelete={() => {
                        if (window.confirm(`Delete ${deal.name}?`)) void deleteDeal(deal.id);
                      }}
                    />
                  ))}
                </PipelineColumn>
              );
            })}
          </div>
        </DndContext>
      ) : <EmptyState>No pipeline stages configured.</EmptyState>}

      {editing && (
        <Modal title={editing === 'new' ? 'New deal' : 'Edit deal'} onClose={() => setEditing(null)}>
          <DealForm
            deal={editing === 'new' ? null : editing}
            contacts={contacts}
            companies={companies}
            users={users}
            stages={stages}
            onSubmit={saveDeal}
          />
        </Modal>
      )}
      {reminderDeal && (
        <Modal title="Set reminder" onClose={() => setReminderDeal(null)}>
          <ReminderForm onSubmit={saveReminder} currentUserId={currentUser?.uid ?? ''} />
        </Modal>
      )}
    </CrmShell>
  );
}

function PipelineColumn({ stage, deals, children }: { stage: string; deals: Deal[]; children: React.ReactNode }) {
  const { isOver, setNodeRef } = useDroppable({ id: stage });
  const value = deals.reduce((sum, deal) => sum + deal.value, 0);
  return (
    <section ref={setNodeRef} className={`flex min-h-[28rem] w-72 shrink-0 flex-1 flex-col rounded-lg border border-gray-200 bg-white p-3 shadow-sm ${isOver ? 'ring-2 ring-primary' : ''}`}>
      <div className="mb-3 flex items-start justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">{stage}</h2>
          <p className="text-xs text-gray-500">{deals.length} deals</p>
        </div>
        <span className="text-xs font-semibold text-primary">{formatMoney(value)}</span>
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function DealDetailPage({ dealId }: { dealId: string }) {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [deals, setDeals] = useState<Deal[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [users, setUsers] = useState<AppUser[]>([]);
  const [stages, setStages] = useState<string[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [editing, setEditing] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubs = [
      subscribeToDeals(setDeals, (err) => setError(err.message)),
      subscribeToContacts(setContacts, (err) => setError(err.message)),
      subscribeToCompanies(setCompanies, (err) => setError(err.message)),
      subscribeToPipelineSettings((settings) => setStages(settings.stages), (err) => setError(err.message)),
      subscribeToActivitiesForDeal(dealId, setActivities, (err) => setError(err.message)),
    ];
    fetchUsers().then(setUsers).catch((err) => {
      setError(err.message);
      logGlobalError(err, { context: 'DealDetailPage.fetchUsers' });
    });
    return () => unsubs.forEach((unsub) => unsub());
  }, [dealId]);

  const deal = deals.find((item) => item.id === dealId) ?? null;
  const contact = deal?.contactId ? contacts.find((item) => item.id === deal.contactId) : null;
  const company = deal?.companyId ? companies.find((item) => item.id === deal.companyId) : null;
  const owner = deal?.ownerId ? users.find((item) => item.uid === deal.ownerId) : null;

  async function saveDeal(values: DealFormValues) {
    setEditing(false);
    try {
      await updateDeal(dealId, {
        ...values,
        contactId: values.contactId || null,
        companyId: values.companyId || null,
        ownerId: values.ownerId || null,
        value: Number(values.value) || 0,
      });
    } catch (err: any) {
      setError(err?.message || 'Failed to save deal');
      logGlobalError(err, { context: 'DealDetailPage.saveDeal' });
    }
  }

  async function removeDeal() {
    if (!deal || !window.confirm(`Delete ${deal.name}?`)) return;
    await deleteDeal(deal.id);
    navigate('/deals');
  }

  async function saveActivity(values: { type: any; dealId: string | null; subject: string; body: string; activityDate: Date }) {
    if (!deal?.contactId) {
      setError('A deal must be linked to a contact before activity can be logged.');
      return;
    }
    const contactId = deal.contactId;
    setActivityOpen(false);
    try {
      await createActivity({ ...values, dealId, contactId }, currentUser?.uid ?? '');
    } catch (err: any) {
      setError(err?.message || 'Failed to log activity');
      logGlobalError(err, { context: 'DealDetailPage.saveActivity' });
    }
  }

  async function saveReminder(values: { assignedTo: string; dueDate: Date; note: string }) {
    setReminderOpen(false);
    try {
      await createReminder({ ...values, entityType: 'deal', entityId: dealId }, currentUser?.uid ?? '');
    } catch (err: any) {
      setError(err?.message || 'Failed to save reminder');
      logGlobalError(err, { context: 'DealDetailPage.saveReminder' });
    }
  }

  return (
    <CrmShell>
      <PageHeader
        title={deal?.name ?? 'Deal'}
        description={deal ? `${deal.stage} · ${formatMoney(deal.value)}` : undefined}
        action={deal && <div className="flex flex-wrap gap-2"><Button variant="secondary" onClick={() => setEditing(true)}>Edit</Button><Button variant="secondary" onClick={() => setReminderOpen(true)}>Set reminder</Button><Button onClick={() => setActivityOpen(true)}>Log activity</Button><Button variant="danger" onClick={removeDeal}>Delete</Button></div>}
      />
      {error && <div className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {!deal ? <EmptyState>Deal not found.</EmptyState> : (
        <div className="grid gap-6 lg:grid-cols-[0.8fr_1.2fr]">
          <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-900">Details</h2>
            <dl className="mt-4 space-y-3 text-sm">
              <DealDetail label="Stage" value={deal.stage} />
              <DealDetail label="Value" value={formatMoney(deal.value)} />
              <DealDetail label="Expected close" value={formatDate(deal.expectedCloseDate)} />
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Contact</dt>
                <dd className="mt-1 text-gray-800">{contact ? <Link className="text-primary hover:underline" to={`/contacts/${contact.id}`}>{contact.firstName} {contact.lastName}</Link> : '—'}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Company</dt>
                <dd className="mt-1 text-gray-800">{company ? <Link className="text-primary hover:underline" to={`/companies/${company.id}`}>{company.name}</Link> : '—'}</dd>
              </div>
              <DealDetail label="Owner" value={owner?.displayName || owner?.email || '—'} />
              <DealDetail label="Notes" value={deal.notes} />
            </dl>
          </section>
          <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-900">Activity</h2>
            <div className="mt-4 space-y-3">
              {activities.length ? activities.map((activity) => (
                <article key={activity.id} className="rounded border border-gray-200 p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-sm font-medium text-gray-900">{activity.subject || activity.type}</div>
                    <div className="text-xs text-gray-500">{formatDate(activity.activityDate)}</div>
                  </div>
                  <div className="mt-1 text-xs uppercase tracking-wide text-gray-400">{activity.type.replace('_', ' ')}</div>
                  {activity.body && <p className="mt-2 whitespace-pre-wrap text-sm text-gray-600">{activity.body}</p>}
                </article>
              )) : <p className="text-sm text-gray-500">No activity yet.</p>}
            </div>
          </section>
        </div>
      )}
      {editing && deal && (
        <Modal title="Edit deal" onClose={() => setEditing(false)}>
          <DealForm deal={deal} contacts={contacts} companies={companies} users={users} stages={stages} onSubmit={saveDeal} />
        </Modal>
      )}
      {activityOpen && deal && (
        <Modal title="Log activity" onClose={() => setActivityOpen(false)}>
          <ActivityForm deals={[deal]} onSubmit={saveActivity} />
        </Modal>
      )}
      {reminderOpen && (
        <Modal title="Set reminder" onClose={() => setReminderOpen(false)}>
          <ReminderForm onSubmit={saveReminder} currentUserId={currentUser?.uid ?? ''} />
        </Modal>
      )}
    </CrmShell>
  );
}

function DealDetail({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-1 whitespace-pre-wrap text-gray-800">{value || '—'}</dd>
    </div>
  );
}

function DealCard({
  deal,
  contact,
  company,
  owner,
  detailUrl,
  onEdit,
  onReminder,
  onDelete,
}: {
  deal: Deal;
  contact?: Contact;
  company?: Company;
  owner?: AppUser;
  detailUrl: string;
  onEdit: () => void;
  onReminder: () => void;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: deal.id });
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const style = { transform: CSS.Translate.toString(transform) };
  const ownerLabel = owner?.displayName || owner?.email || 'Unassigned';

  useEffect(() => {
    if (!menuOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  function runAction(action: () => void) {
    setMenuOpen(false);
    action();
  }

  return (
    <article
      ref={setNodeRef}
      style={style}
      className={`relative rounded-lg border border-gray-200 bg-white p-3 shadow-sm ${isDragging ? 'opacity-60' : ''}`}
    >
      <div className="flex items-start justify-between gap-2">
        <button type="button" className="flex-1 cursor-grab text-left" {...listeners} {...attributes}>
          <h3 className="text-sm font-semibold text-gray-900">{deal.name}</h3>
          <p className="mt-1 text-xs text-gray-500">{contact ? `${contact.firstName} ${contact.lastName}` : company?.name || 'No contact'}</p>
        </button>
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            aria-label="Deal actions"
            className="flex h-7 w-7 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <MoreVertical className="h-4 w-4" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 z-10 mt-1 w-32 overflow-hidden rounded-md border border-gray-200 bg-white py-1 shadow-lg">
              <button type="button" className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50" onClick={() => runAction(() => navigate(detailUrl))}>View</button>
              <button type="button" className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50" onClick={() => runAction(onEdit)}>Edit</button>
              <button type="button" className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50" onClick={() => runAction(onReminder)}>Reminder</button>
              <button type="button" className="block w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50" onClick={() => runAction(onDelete)}>Delete</button>
            </div>
          )}
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-primary">{formatMoney(deal.value)}</span>
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary" title={ownerLabel}>
          {ownerLabel.charAt(0).toUpperCase()}
        </span>
      </div>
    </article>
  );
}

interface DealFormValues {
  name: string;
  contactId: string | null;
  companyId: string | null;
  stage: string;
  value: number;
  expectedCloseDate: Date | null;
  ownerId: string | null;
  notes: string;
}

function DealForm({
  deal,
  contacts,
  companies,
  users,
  stages,
  onSubmit,
}: {
  deal: Deal | null;
  contacts: Contact[];
  companies: Company[];
  users: AppUser[];
  stages: string[];
  onSubmit: (values: DealFormValues) => Promise<void>;
}) {
  const [values, setValues] = useState<DealFormValues>({
    name: deal?.name ?? '',
    contactId: deal?.contactId ?? null,
    companyId: deal?.companyId ?? null,
    stage: deal?.stage ?? stages[0] ?? 'Outreach',
    value: deal?.value ?? 0,
    expectedCloseDate: deal?.expectedCloseDate ?? null,
    ownerId: deal?.ownerId ?? null,
    notes: deal?.notes ?? '',
  });
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      await onSubmit(values);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
      <Field label="Deal name"><input className={inputClass} value={values.name} onChange={(e) => setValues({ ...values, name: e.target.value })} required /></Field>
      <Field label="Stage"><select className={inputClass} value={values.stage} onChange={(e) => setValues({ ...values, stage: e.target.value })}>{stages.map((stage) => <option key={stage} value={stage}>{stage}</option>)}</select></Field>
      <Field label="Contact"><select className={inputClass} value={values.contactId ?? ''} onChange={(e) => setValues({ ...values, contactId: e.target.value || null })}><option value="">No contact</option>{contacts.map((contact) => <option key={contact.id} value={contact.id}>{contact.firstName} {contact.lastName}</option>)}</select></Field>
      <Field label="Company"><select className={inputClass} value={values.companyId ?? ''} onChange={(e) => setValues({ ...values, companyId: e.target.value || null })}><option value="">No company</option>{companies.map((company) => <option key={company.id} value={company.id}>{company.name}</option>)}</select></Field>
      <Field label="Value"><input className={inputClass} type="number" min="0" value={values.value} onChange={(e) => setValues({ ...values, value: Number(e.target.value) })} /></Field>
      <Field label="Expected close"><input className={inputClass} type="date" value={dateInputValue(values.expectedCloseDate)} onChange={(e) => setValues({ ...values, expectedCloseDate: parseDateInput(e.target.value) })} /></Field>
      <Field label="Owner"><select className={inputClass} value={values.ownerId ?? ''} onChange={(e) => setValues({ ...values, ownerId: e.target.value || null })}><option value="">Unassigned</option>{users.map((user) => <option key={user.uid} value={user.uid}>{user.displayName || user.email || user.uid}</option>)}</select></Field>
      <div className="sm:col-span-2"><Field label="Notes"><textarea className={inputClass} rows={4} value={values.notes} onChange={(e) => setValues({ ...values, notes: e.target.value })} /></Field></div>
      <div className="sm:col-span-2"><Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save deal'}</Button></div>
    </form>
  );
}
